import React, { useEffect, useRef, useState } from 'react';
import { Download, AlertCircle } from 'lucide-react';

import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { createClientLogger } from '~/utils/logger.client';
import { toast } from 'sonner';
import { formatDateForFilename } from '~/utils/date';

let logger: ReturnType<typeof createClientLogger> | null = null;
const getLogger = () => {
  if (!logger && typeof window !== 'undefined') {
    try {
      logger = createClientLogger('FastVideoPlayer');
    } catch (error) {
      console.warn('Failed to create logger, using console fallback:', error);
      return {
        info: (msg: string, data?: any) => console.info(`[FastVideoPlayer] ${msg}`, data?.metadata || ''),
        warn: (msg: string, data?: any) => console.warn(`[FastVideoPlayer] ${msg}`, data?.metadata || ''),
        error: (msg: string, data?: any) => console.error(`[FastVideoPlayer] ${msg}`, data?.metadata || ''),
        debug: (msg: string, data?: any) => console.debug(`[FastVideoPlayer] ${msg}`, data?.metadata || ''),
      };
    }
  }
  return logger || {
    info: (msg: string, data?: any) => console.info(`[FastVideoPlayer] ${msg}`, data?.metadata || ''),
    warn: (msg: string, data?: any) => console.warn(`[FastVideoPlayer] ${msg}`, data?.metadata || ''),
    error: (msg: string, data?: any) => console.error(`[FastVideoPlayer] ${msg}`, data?.metadata || ''),
    debug: (msg: string, data?: any) => console.debug(`[FastVideoPlayer] ${msg}`, data?.metadata || ''),
  };
};

interface FastVideoPlayerProps {
  fileId: string;
  thumbnailUrl?: string | null;
  showDownload?: boolean;
  className?: string;
  title?: string;
  description?: string;
  autoPlay?: boolean;
  muted?: boolean;
}

const LOAD_TIMEOUT_MS = 15_000;

export default function FastVideoPlayer({
  fileId,
  thumbnailUrl,
  showDownload = false,
  className = '',
  title = 'Your Recording',
  description = 'Fast encrypted video streaming',
  autoPlay = false,
  muted = false,
}: FastVideoPlayerProps) {
  const { _ } = useLingui();
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isHidden, setIsHidden] = useState(false);
  const [loadStarted, setLoadStarted] = useState(false);
  const videoRef = useRef<any>(null);

  // Use the direct API endpoint which handles authentication server-side
  // Always use relative URL to avoid hydration mismatch
  const streamingUrl = `/api/video/${fileId}`;

  // Loading timeout: hide the player if video doesn't become ready within LOAD_TIMEOUT_MS
  useEffect(() => {
    if (isReady || isHidden) return;

    const timeout = setTimeout(() => {
      if (!isReady) {
        getLogger().warn('Video load timeout - hiding player', {
          metadata: {
            fileId,
            streamingUrl,
            isLoading,
            isReady,
            error,
            timeoutMs: LOAD_TIMEOUT_MS,
            timestamp: new Date().toISOString()
          }
        });
        setIsHidden(true);
      }
    }, LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [isReady, isHidden, fileId]);

  // Initialize component - just log, don't force anything
  useEffect(() => {
    if (!fileId) {
      setError('No video file ID provided');
      getLogger().error('FastVideoPlayer - No file ID provided', {
        metadata: {
          timestamp: new Date().toISOString()
        }
      });
      return;
    }

    getLogger().info('FastVideoPlayer initialized', {
      metadata: {
        fileId,
        streamingUrl,
        timestamp: new Date().toISOString()
      }
    });
  }, [fileId, streamingUrl]);

  // Native video element event handlers
  const handleLoadStart = () => {
    setLoadStarted(true);
    getLogger().info('Video load started', {
      metadata: {
        fileId,
        streamingUrl,
        timestamp: new Date().toISOString()
      }
    });

    // Respect the muted prop from parent
    const video = videoRef.current;
    if (video) {
      video.muted = muted;
      video.volume = muted ? 0 : 1;
    }
  };

  const handleLoadedMetadata = () => {
    setIsLoading(false);
    setIsReady(true);
    getLogger().info('Video metadata loaded', {
      metadata: {
        fileId,
        streamingUrl,
        duration: videoRef.current?.duration,
        videoWidth: videoRef.current?.videoWidth,
        videoHeight: videoRef.current?.videoHeight,
        timestamp: new Date().toISOString()
      }
    });
  };

  const handleCanPlay = () => {
    setIsReady(true);
    setIsLoading(false);
    setError(null);
    getLogger().info('Video ready to play', {
      metadata: {
        fileId,
        streamingUrl,
        duration: videoRef.current?.duration,
        timestamp: new Date().toISOString()
      }
    });
  };

  const [retryCount, setRetryCount] = useState(0);
  const maxAutoRetries = 2;

  const handleError = () => {
    const video = videoRef.current;
    const error = video?.error;

    // Detect if we're on mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);

    // Check if it's an audio decode error
    if (error && error.code === 3 && (error.message.includes('audio') || error.message.includes('AUDIO'))) {
      getLogger().warn('Audio decode error - attempting to continue with video only', {
        metadata: {
          fileId,
          error: error.message,
          errorCode: error.code,
          networkState: video.networkState,
          readyState: video.readyState,
          isMobile,
          isIOS,
          timestamp: new Date().toISOString()
        }
      });

      // Try to force the video to continue despite audio error
      if (video) {
        video.muted = muted;
        video.volume = muted ? 0 : 1;

        // Check if we have video metadata (video stream is working)
        setTimeout(() => {
          if (video.readyState >= 1) { // HAVE_METADATA
            setIsLoading(false);
            setError(null);
            getLogger().info('Video continued despite audio error', {
              metadata: {
                fileId,
                readyState: video.readyState,
                duration: video.duration,
                videoWidth: video.videoWidth,
                videoHeight: video.videoHeight
              }
            });
          }
        }, 1000);
      }
      return; // Don't set error state for audio errors
    }

    // Check for MEDIA_ERR_SRC_NOT_SUPPORTED (error code 4) - common on mobile/iOS
    if (error && error.code === 4 && retryCount < maxAutoRetries) {
      getLogger().warn('Media source not supported error - attempting automatic retry', {
        metadata: {
          fileId,
          errorCode: error.code,
          errorMessage: error.message,
          retryCount,
          maxAutoRetries,
          isMobile,
          isIOS,
          userAgent: navigator.userAgent,
          videoFormat: video?.currentSrc,
          timestamp: new Date().toISOString()
        }
      });

      // On mobile, especially iOS, try with different loading strategy
      setTimeout(() => {
        if (video) {
          setRetryCount(prev => prev + 1);
          setIsLoading(true);
          setError(null);

          // For iOS, try forcing preload and muted autoplay
          if (isIOS || isMobile) {
            video.muted = true; // iOS requires muted for autoplay
            video.preload = 'auto'; // Force full preload on mobile
          }

          video.load();
          getLogger().info('Automatic retry initiated with mobile-specific settings', {
            metadata: {
              fileId,
              retryAttempt: retryCount + 1,
              isMuted: video.muted,
              preload: video.preload,
              isMobile,
              isIOS
            }
          });
        }
      }, 2000); // 2 second delay for mobile networks

      return;
    }

    // Log the error for debugging, then hide the player
    const errorMessage = error
      ? `Video error: ${error.code} - ${error.message}`
      : 'Failed to load video stream';

    setError(errorMessage);
    setIsLoading(false);
    getLogger().error('Video error - hiding player', {
      metadata: {
        fileId,
        streamingUrl,
        errorCode: error?.code,
        errorMessage: error?.message,
        networkState: video?.networkState,
        readyState: video?.readyState,
        retryCount,
        isMobile,
        isIOS,
        userAgent: navigator.userAgent,
        videoFormat: video?.currentSrc,
        timestamp: new Date().toISOString()
      }
    });
  };

  const handlePlay = () => {
    getLogger().info('Video playback started', {
      metadata: {
        fileId,
        streamingUrl,
        currentTime: videoRef.current?.currentTime,
        timestamp: new Date().toISOString()
      }
    });
  };

  const handlePause = () => {
    getLogger().info('Video playback paused', {
      metadata: {
        fileId,
        streamingUrl,
        currentTime: videoRef.current?.currentTime,
        timestamp: new Date().toISOString()
      }
    });
  };

  const handleDownload = async () => {
    try {
      getLogger().info('Video download initiated', { metadata: { fileId } });

      const downloadUrl = `/api/video/${fileId}`;

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `speech-practice-${formatDateForFilename()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      toast.success(_(msg`Video download started`));
      getLogger().info('Video download link clicked', { metadata: { fileId, downloadUrl } });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error(_(msg`Failed to start video download`));
      getLogger().error('Video download failed', {
        metadata: { fileId, error: errorMessage }
      });
    }
  };

  // Hide completely on error, timeout, or missing fileId
  if (error || isHidden) {
    return null;
  }

  return (
    <Card className={` ${className}`}>
      <CardContent className="p-0">
        <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
          <video
            ref={videoRef}
            src={streamingUrl}
            controls
            muted={muted}
            autoPlay={autoPlay}
            playsInline
            webkit-playsinline="true"
            x-webkit-airplay="allow"
            poster={thumbnailUrl || undefined}
            className="w-full h-full object-contain rounded-lg"
            onLoadStart={handleLoadStart}
            onLoadedMetadata={handleLoadedMetadata}
            onCanPlay={handleCanPlay}
            onError={handleError}
            onPlay={handlePlay}
            onPause={handlePause}
            preload="auto"
            crossOrigin="use-credentials"
            style={{
              borderRadius: '0.5rem',
            }}
          />

          {/* Loading overlay */}
          {isLoading && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white rounded-lg backdrop-blur-sm">
              <div className="relative">
                <img
                  src="/images/pavone-mini-white-v2.png"
                  alt="Pavone"
                  className="w-16 h-16 opacity-70 drop-shadow-sm"
                />
                {/* Pulsating ring around logo */}
                <div className="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping opacity-40"></div>
                <div className="absolute inset-0 rounded-full border border-blue-300 animate-pulse opacity-60"></div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
